import {Box, Flex, Text} from 'exsportia-components'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'

import {theme} from '../../../theme/theme'
import {useLocale} from '../../../utils/i18n'
import {ExsportiaIcon} from '../exsportia-icon/exsportia-icon'

const SelectContainer = styled(Box)`
  position: relative;
  flex: 1;
`

const SelectedValueBox = styled(Box)<{isOpen: boolean}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border: ${theme.borders['default-border']};
  border-radius: 12px;
  height: 48px;
  padding: 0 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-color: ${props => (props.isOpen ? theme.colors.primary : 'rgba(0, 16, 61, 0.12)')};

  &:hover {
    border-color: ${theme.colors.primary}88;
  }
`

const DropdownList = styled(Box)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 200px;
  background: white;
  border: ${theme.borders['default-border']};
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  z-index: 1000;

  @media (max-width: 768px) {
    top: auto;
    bottom: calc(100% + 4px);
  }

  /* Hide scrollbar for cleaner look but keep functionality */
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 10px;
  }
`

const OptionRow = styled(Box)<{isSelected: boolean}>`
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => (props.isSelected ? theme.colors['primary-secondary'] : 'transparent')};

  &:hover {
    background: ${props => (props.isSelected ? theme.colors['primary-secondary'] : '#f5f5f5')};
  }
`

type CustomSelectProps = {
  options: (string | number)[]
  value: string | number
  onChange: (value: string | number) => void
  flex?: number | string
}

const CustomSelect: FC<CustomSelectProps> = ({options, value, onChange, flex = 1}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <SelectContainer ref={containerRef} style={{flex}}>
      <SelectedValueBox isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <Text text={String(value)} settings={{fontSize: 'body'}} />
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            transform: isOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        >
          <ExsportiaIcon
            icon='chevronDown'
            color='text-secondary'
            w={14} h={14}
          />
        </Box>
      </SelectedValueBox>
      {isOpen && (
        <DropdownList>
          {options.map((option) => (
            <OptionRow
              key={option}
              isSelected={option === value}
              onClick={() => {
                onChange(option)
                setIsOpen(false)
              }}
            >
              <Text text={String(option)} settings={{fontSize: 'body'}} />
            </OptionRow>
          ))}
        </DropdownList>
      )}
    </SelectContainer>
  )
}

export type DOBPickerProps = {
  value: DateTime | null
  onChange: (date: DateTime) => void
}

export const DOBPicker: FC<DOBPickerProps> = ({value, onChange}) => {
  const {l} = useLocale()
  const luxonValue = useMemo(() => value || DateTime.now(), [value])

  const years = useMemo(() => {
    const arr = []
    const currentYear = DateTime.now().year
    for (let i = currentYear; i >= 1900; i--) {
      arr.push(i)
    }
    return arr
  }, [])

  const months = useMemo(() => {
    return [
      l('months_january', {defaultMessage: 'January'}),
      l('months_february', {defaultMessage: 'February'}),
      l('months_march', {defaultMessage: 'March'}),
      l('months_april', {defaultMessage: 'April'}),
      l('months_may', {defaultMessage: 'May'}),
      l('months_june', {defaultMessage: 'June'}),
      l('months_july', {defaultMessage: 'July'}),
      l('months_august', {defaultMessage: 'August'}),
      l('months_september', {defaultMessage: 'September'}),
      l('months_october', {defaultMessage: 'October'}),
      l('months_november', {defaultMessage: 'November'}),
      l('months_december', {defaultMessage: 'December'})
    ]
  }, [l])

  const days = useMemo(() => {
    const arr = []
    const maxDays = luxonValue.daysInMonth || 31
    for (let i = 1; i <= maxDays; i++) {
        arr.push(i)
    }
    return arr
  }, [luxonValue.month, luxonValue.year])

  const handleSelect = useCallback((type: 'day' | 'month' | 'year', newVal: string | number) => {
    let updatedDate = luxonValue
    if (type === 'day') {
      updatedDate = luxonValue.set({day: Number(newVal)})
    }
    if (type === 'month') {
      updatedDate = luxonValue.set({month: months.indexOf(newVal as string) + 1})
    }
    if (type === 'year') {
      updatedDate = luxonValue.set({year: Number(newVal)})
    }

    if (updatedDate.day > (updatedDate.daysInMonth || 31)) {
      updatedDate = updatedDate.set({day: updatedDate.daysInMonth})
    }

    if (updatedDate.isValid) {
      onChange(updatedDate)
    }
  }, [luxonValue, months, onChange])

  return (
    <Flex width='100%' gap='8px' alignItems='center'>
      <CustomSelect
        flex={1}
        options={days}
        value={luxonValue.day}
        onChange={(v) => handleSelect('day', v)}
      />
      <CustomSelect
        flex={1.8}
        options={months}
        value={months[luxonValue.month - 1]}
        onChange={(v) => handleSelect('month', v)}
      />
      <CustomSelect
        flex={1.2}
        options={years}
        value={luxonValue.year}
        onChange={(v) => handleSelect('year', v)}
      />
    </Flex>
  )
}
